/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef } from 'react';
import { ActivityIndicator, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { NumericTextInput } from '../../../shared/ui/NumericTextInput';
import type { CashVm } from './cashManagement.types';
import { cashArqueoCopy, formatOpenTurnoLastCloseInfo } from './cashArqueoCopy';
import { formatMoney } from './cashFormatMoney';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { cashStyles } from './cashManagement.styles';

type Props = {
  vm: CashVm;
  footer?: React.ReactNode;
  /**
   * 'modal': card flotante con header ticket sobre el contexto — proto 1b.
   * 'inline' (default): panel plano para el layout mobile apilado.
   */
  chrome?: 'modal' | 'inline';
  /** Solo chrome='modal': cierra el modal de apertura (X). */
  onClose?: () => void;
};

export function CashOpenTurnoForm({ vm, footer, chrome = 'inline', onClose }: Props) {
  const { layoutWebShell } = useContentLayout('dataWide');
  const mobile = !layoutWebShell;
  const inputRef = useRef<any>(null);
  const lastCloseAmount = vm.cashSummary?.lastClosedSession?.closingAmount;
  // El server no respondió el summary: "sin turno" es desconocido, no confirmado
  // (VUELTO-NEGOCIO-3Q — abrir a ciegas terminaba en rechazo del server).
  const unverified = vm.cashSummary?.remoteStatus === 'unavailable';

  useEffect(() => {
    if (vm.canEdit && !vm.openingCash) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [vm.canEdit, vm.openingCash]);

  const infoBox =
    lastCloseAmount != null ? (
      <View
        style={panelStyles.turnoOpenInfoBox}
        accessibilityLabel={formatOpenTurnoLastCloseInfo(lastCloseAmount)}
      >
        <Text style={panelStyles.turnoOpenInfoText}>
          El último cierre quedó en{' '}
          <Text style={panelStyles.turnoOpenInfoStrong}>{formatMoney(lastCloseAmount)}</Text>
          . Usalo como fondo o cargá otro monto.
        </Text>
      </View>
    ) : null;

  const unverifiedNotice = unverified ? (
    <View style={panelStyles.turnoOpenInfoBox} accessibilityLiveRegion="polite">
      <Text style={panelStyles.turnoOpenInfoText}>
        No pudimos verificar si hay un turno abierto. Actualizá la pantalla para reintentar.
      </Text>
    </View>
  ) : null;

  const submitButton = (
    <TouchableOpacity
      style={[
        mobile ? cashStyles.btnMobilePrimary : styles.primaryButton,
        chrome === 'modal' ? null : panelStyles.turnoPrimaryAction,
        (!vm.canEdit || vm.openingCash || unverified) && styles.disabled,
      ]}
      onPress={vm.handleOpenCash}
      disabled={!vm.canEdit || vm.openingCash || unverified}
      accessibilityLabel="Abrir turno"
      accessibilityRole="button"
      accessibilityState={{ busy: vm.openingCash }}
      testID="caja.open.submit"
    >
      {vm.openingCash ? (
        <ActivityIndicator color="#FFFFFF" />
      ) : (
        <Text style={mobile ? cashStyles.btnMobilePrimaryText : styles.primaryButtonText}>Abrir turno</Text>
      )}
    </TouchableOpacity>
  );

  const fieldBlock = (
    <View style={styles.inputsBlock}>
      <Text style={panelStyles.turnoContextTitle}>Fondo inicial</Text>
      <View style={panelStyles.turnoOpenMoneyField}>
        <Text style={panelStyles.turnoOpenMoneyPrefix}>$</Text>
        <NumericTextInput
          ref={inputRef}
          kind="decimalMoney"
          value={vm.openingAmount}
          onChangeText={vm.setOpeningAmount}
          placeholder="0"
          placeholderTextColor={ticketColors.ink3}
          style={panelStyles.turnoOpenMoneyInput}
          editable={vm.canEdit}
          accessibilityLabel="Fondo inicial para abrir caja"
          accessibilityRole="none"
          testID="caja.open.fondo-inicial"
        />
      </View>
      {lastCloseAmount != null ? (
        <Text style={panelStyles.turnoCloseHint}>{cashArqueoCopy.openingSuggestedHint}</Text>
      ) : null}
    </View>
  );

  if (chrome === 'modal') {
    return (
      <View style={panelStyles.openModalCard}>
        <View style={panelStyles.openModalHead}>
          <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
            <Text style={panelStyles.openModalKicker}>Caja</Text>
            <Text style={panelStyles.openModalTitle} accessibilityRole="header">
              Abrir caja
            </Text>
            <Text style={panelStyles.openModalSub}>
              {unverified
                ? 'Estado de caja sin verificar'
                : vm.branchName
                  ? `Sin turno abierto en ${vm.branchName}`
                  : 'Sin turno abierto'}
            </Text>
          </View>
          {onClose ? (
            <TouchableOpacity
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
              style={panelStyles.openModalClose}
              testID="caja.open.modal-close"
            >
              <Ionicons name="close" size={20} color={ticketColors.ink3} />
            </TouchableOpacity>
          ) : null}
        </View>
        <View style={panelStyles.openModalBody}>
          {unverifiedNotice}
          {infoBox}
          {fieldBlock}
          {footer}
        </View>
        <View style={panelStyles.openModalFoot}>{submitButton}</View>
      </View>
    );
  }

  return (
    <View style={[panelStyles.turnoOpenPanel, mobile && panelStyles.turnoOpenPanelMobile]}>
      <Text style={panelStyles.turnoOpenTitle}>Abrir caja</Text>
      {unverifiedNotice}
      {infoBox}
      {fieldBlock}
      {submitButton}
      {footer}
    </View>
  );
}
